import { onBeforeUnmount, onMounted, ref } from "vue";
/** Points sampled from the Lamar mark, orbiting as a halo or sphere and gathering into the logo. */
const POINT_COUNT = 5000;
const ORB_RADIUS = 250;
const MARK_SPAN = 540;
// One cycle: the orb gathers into the mark, the mark holds, it scatters back out and
// the orb holds. The orb alternates between halo and sphere from one cycle to the next.
const GATHER_SECONDS = 3.52;
const MARK_HOLD_SECONDS = 6.4;
const SCATTER_SECONDS = 2.88;
const ORB_HOLD_SECONDS = 4.2;
const CYCLE_SECONDS = GATHER_SECONDS + MARK_HOLD_SECONDS + SCATTER_SECONDS + ORB_HOLD_SECONDS;
// Render above the display size and let the browser downscale, which is what
// sharpens the dots; capped so a large display cannot blow up the fill cost.
// gl_PointSize is in framebuffer pixels, so the shader is given the
// framebuffer-to-CSS ratio and sizes points in CSS pixels, so a point does not
// shrink as the supersample or the device pixel ratio grows.
const SUPERSAMPLE = 1.2;
const MAX_BACKING_PIXELS = 5.0e6;
// The orb is the widest thing drawn, so fitting its radius fits the mark too.
const CONTENT_HALF_EXTENT = 300;
const PHYSICS_STEP = 1 / 60;
const MAX_PHYSICS_STEPS = 5;
// The plastic number drives the R2 sequence, which spreads successive 2D points evenly.
const PLASTIC = 1.324717957244746;
// R2 needs about POINT_COUNT / coverage samples to place every point. Marks covering less
// than MIN_MARK_COVERAGE of the sampling grid show the static mark instead; for the rest,
// twice the worst expected count leaves ample headroom, so the cap only guards the main thread.
const MIN_MARK_COVERAGE = 1 / 64;
const MAX_SAMPLE_ATTEMPTS = (2 * POINT_COUNT) / MIN_MARK_COVERAGE;
const VERTEX_SHADER = `#version 300 es
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aCol;
layout(location=2) in float aSeed;
uniform vec2 uRes;
uniform float uPxPerCss;
uniform float uFit;
out vec3 vCol;
out float vA;
void main() {
  float d = 1.0 / (1.0 - aPos.z * 0.0016);
  gl_Position = vec4((aPos.xy * d * uFit) / (uRes * 0.5), 0.0, 1.0);
  gl_PointSize = (1.1 + 1.1 * d) * (0.72 + 0.56 * fract(aSeed * 7.3)) * uPxPerCss;
  vCol = aCol;
  vA = clamp(0.42 + 1.05 * (d - 0.82), 0.10, 1.0);
}`;
const FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec3 vCol;
in float vA;
out vec4 o;
void main() {
  float a = smoothstep(0.5, 0.06, length(gl_PointCoord - 0.5));
  o = vec4(vCol * vA * a, a * vA);
}`;
function compile(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
}
function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
export function useMarkField(host, canvas, markUrl) {
    // Covers every reason the point field cannot run: no WebGL2, a failed link,
    // a lost context, or a mark image that failed to load or had no opaque pixels.
    const markFieldUnavailable = ref(false);
    let gl = null;
    let raf = 0;
    let running = false;
    let onScreen = true;
    let painted = false;
    let observer = null;
    let resizeObserver = null;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const pos = new Float32Array(POINT_COUNT * 3);
    const col = new Float32Array(POINT_COUNT * 3);
    const seed = new Float32Array(POINT_COUNT);
    const phase = new Float32Array(POINT_COUNT);
    const markX = new Float32Array(POINT_COUNT);
    const markY = new Float32Array(POINT_COUNT);
    const markZ = new Float32Array(POINT_COUNT);
    const haloX = new Float32Array(POINT_COUNT);
    const haloY = new Float32Array(POINT_COUNT);
    const haloZ = new Float32Array(POINT_COUNT);
    const sphereX = new Float32Array(POINT_COUNT);
    const sphereY = new Float32Array(POINT_COUNT);
    const sphereZ = new Float32Array(POINT_COUNT);
    const curX = new Float32Array(POINT_COUNT);
    const curY = new Float32Array(POINT_COUNT);
    const curZ = new Float32Array(POINT_COUNT);
    const prevX = new Float32Array(POINT_COUNT);
    const prevY = new Float32Array(POINT_COUNT);
    const prevZ = new Float32Array(POINT_COUNT);
    const velX = new Float32Array(POINT_COUNT);
    const velY = new Float32Array(POINT_COUNT);
    const velZ = new Float32Array(POINT_COUNT);
    const pointer = { x: 0, y: 0, inside: false, nx: 0, ny: 0 };
    let ready = false;
    let spin = 0;
    let startedAt = 0;
    let lastFrame = 0;
    let accumulator = 0;
    let posBuffer = null;
    let resolution = null;
    let pxPerCss = null;
    let fitUniform = null;
    let fit = 1;
    function buildGeometry() {
        const golden = 1 + Math.sqrt(5);
        for (let k = 0; k < POINT_COUNT; k++) {
            const theta = (2 * Math.PI * k) / golden;
            const phi = Math.acos(1 - (2 * (k + 0.5)) / POINT_COUNT);
            sphereX[k] = ORB_RADIUS * Math.sin(phi) * Math.cos(theta);
            sphereY[k] = ORB_RADIUS * Math.sin(phi) * Math.sin(theta);
            sphereZ[k] = ORB_RADIUS * Math.cos(phi);
            const around = ((2 * Math.PI * k) / POINT_COUNT) * 7;
            const through = 2 * Math.PI * (k / POINT_COUNT) * 13;
            const tube = 46;
            const ringRadius = ORB_RADIUS * 1.02 + tube * Math.cos(through);
            const rawY = tube * Math.sin(through) * 1.1;
            const rawZ = ringRadius * Math.sin(around);
            const tilt = -0.5;
            haloX[k] = ringRadius * Math.cos(around);
            haloY[k] = rawY * Math.cos(tilt) - rawZ * Math.sin(tilt);
            haloZ[k] = rawY * Math.sin(tilt) + rawZ * Math.cos(tilt);
        }
    }
    function sampleMark(image) {
        const size = 260;
        const scratch = document.createElement("canvas");
        scratch.width = size;
        scratch.height = size;
        const ctx = scratch.getContext("2d", { willReadFrequently: true });
        if (!ctx)
            return false;
        const scale = Math.min(size / image.width, size / image.height);
        ctx.drawImage(image, (size - image.width * scale) / 2, (size - image.height * scale) / 2, image.width * scale, image.height * scale);
        const pixels = ctx.getImageData(0, 0, size, size).data;
        let opaqueCount = 0;
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] > 60)
                opaqueCount++;
        }
        if (opaqueCount < size * size * MIN_MARK_COVERAGE)
            return false;
        // Independent random picks clump: over a third of the points landed on top of a
        // neighbour and read as bright splotches beside bare patches. Walking the R2
        // sequence over the mark spaces them evenly, and a nudge of a quarter of the
        // mean spacing breaks up the faint diagonal rows the sequence leaves.
        const nudge = 0.5 * Math.sqrt(opaqueCount / POINT_COUNT) * (MARK_SPAN / size);
        let n = 0;
        for (let k = 0; k < POINT_COUNT; k++) {
            let i;
            let px;
            let py;
            do {
                if (n === MAX_SAMPLE_ATTEMPTS)
                    return false;
                px = ((0.5 + n / PLASTIC) % 1) * size;
                py = ((0.5 + n / (PLASTIC * PLASTIC)) % 1) * size;
                i = ((py | 0) * size + (px | 0)) * 4;
                n++;
            } while (pixels[i + 3] <= 60);
            markX[k] = (px / size - 0.5) * MARK_SPAN + (Math.random() - 0.5) * nudge;
            markY[k] = -((py / size - 0.5) * MARK_SPAN) + (Math.random() - 0.5) * nudge;
            markZ[k] = (Math.random() - 0.5) * 26;
            col[k * 3] = Math.min(1, (pixels[i] / 255) * 1.34);
            col[k * 3 + 1] = Math.min(1, (pixels[i + 1] / 255) * 1.34);
            col[k * 3 + 2] = Math.min(1, (pixels[i + 2] / 255) * 1.34);
            seed[k] = Math.random();
            // Keep this uncorrelated with k: the sphere's fibonacci latitude is a
            // function of k, so any k-derived phase makes the wobble breathe in bands.
            phase[k] = Math.random() * Math.PI * 2;
            curX[k] = prevX[k] = haloX[k];
            curY[k] = prevY[k] = haloY[k];
            curZ[k] = prevZ[k] = haloZ[k];
        }
        return true;
    }
    function resize() {
        if (!gl || !host.value || !canvas.value)
            return;
        const cssWidth = host.value.clientWidth || 720;
        const cssHeight = host.value.clientHeight || 860;
        let scale = Math.min(window.devicePixelRatio || 1, 2) * SUPERSAMPLE;
        const budget = Math.sqrt(MAX_BACKING_PIXELS / (cssWidth * cssHeight));
        if (scale > budget)
            scale = budget;
        const width = Math.max(2, Math.round(cssWidth * scale));
        const height = Math.max(2, Math.round(cssHeight * scale));
        if (canvas.value.width !== width || canvas.value.height !== height) {
            canvas.value.width = width;
            canvas.value.height = height;
        }
        gl.viewport(0, 0, width, height);
        gl.uniform2f(resolution, cssWidth, cssHeight);
        gl.uniform1f(pxPerCss, width / cssWidth);
        // Positions are in a fixed design space, so a panel shorter than the orb
        // clips it. Shrink to fit rather than crop, and never enlarge: the desktop
        // panel is already bigger than the design space.
        fit = Math.min(1, Math.min(cssWidth, cssHeight) / 2 / CONTENT_HALF_EXTENT);
        gl.uniform1f(fitUniform, fit);
    }
    function frame(now) {
        // Guard first: a frame already queued when stop() runs must not reschedule itself.
        if (!running || !gl)
            return;
        raf = requestAnimationFrame(frame);
        if (!ready)
            return;
        // The schedule below runs on wall-clock time, so the integrator has to consume
        // the same amount of time or the two drift apart: clamping a slow frame to a
        // shorter step ran the physics in slow motion, and the scatter window closed
        // before the points had travelled back out to the orb. Fixed sub-steps keep
        // them in lockstep at any frame rate. A display faster than the step lands many
        // frames between steps (half of them at 120Hz), so each frame draws the points
        // blended between the last two steps rather than holding them still.
        const realDt = lastFrame ? Math.min(0.25, (now - lastFrame) / 1000) : PHYSICS_STEP;
        lastFrame = now;
        accumulator += realDt;
        let steps = Math.floor(accumulator / PHYSICS_STEP);
        if (steps > MAX_PHYSICS_STEPS)
            steps = MAX_PHYSICS_STEPS;
        accumulator -= steps * PHYSICS_STEP;
        const blend = Math.min(1, accumulator / PHYSICS_STEP);
        const t = (now - startedAt) / 1000;
        const cycle = Math.floor(t / CYCLE_SECONDS);
        const inCycle = t % CYCLE_SECONDS;
        const scatterAt = GATHER_SECONDS + MARK_HOLD_SECONDS;
        const orbAt = scatterAt + SCATTER_SECONDS;
        let gather;
        if (reduced)
            gather = 1;
        else if (inCycle < GATHER_SECONDS)
            gather = easeInOutCubic(inCycle / GATHER_SECONDS);
        else if (inCycle < scatterAt)
            gather = 1;
        else if (inCycle < orbAt)
            gather = 1 - easeInOutCubic((inCycle - scatterAt) / SCATTER_SECONDS);
        else
            gather = 0;
        // Swap shapes halfway through the mark hold, while no orb is on screen.
        const useSphere = (inCycle < GATHER_SECONDS + MARK_HOLD_SECONDS / 2 ? cycle : cycle + 1) % 2 === 1;
        spin += realDt * (0.3 - 0.27 * gather);
        const cosSpin = Math.cos(spin);
        const sinSpin = Math.sin(spin);
        const tilt = pointer.inside ? pointer.ny * 0.3 : 0.06 * Math.sin(t * 0.25);
        const cosTilt = Math.cos(tilt);
        const sinTilt = Math.sin(tilt);
        const yaw = pointer.inside ? pointer.nx * 0.35 : 0;
        for (let k = 0; k < POINT_COUNT; k++) {
            const ox = useSphere ? sphereX[k] : haloX[k];
            const oy = useSphere ? sphereY[k] : haloY[k];
            const oz = useSphere ? sphereZ[k] : haloZ[k];
            const rx = ox * cosSpin + oz * sinSpin;
            let rz = -ox * sinSpin + oz * cosSpin;
            const ry = oy * cosTilt - rz * sinTilt;
            rz = oy * sinTilt + rz * cosTilt;
            const wobble = 1 + 0.05 * Math.sin(t * 0.9 + phase[k]);
            const mx = markX[k] + yaw * markZ[k] * 0.6;
            const my = markY[k] + tilt * markZ[k] * 0.6;
            const targetX = rx * wobble + (mx - rx * wobble) * gather;
            const targetY = ry * wobble + (my - ry * wobble) * gather;
            const targetZ = rz * wobble + (markZ[k] - rz * wobble) * gather;
            if (reduced) {
                // Only one frame is ever drawn, so the spring would leave the points
                // barely off the orb. Place them on the assembled mark instead.
                curX[k] = prevX[k] = targetX;
                curY[k] = prevY[k] = targetY;
                curZ[k] = prevZ[k] = targetZ;
                pos[k * 3] = curX[k];
                pos[k * 3 + 1] = curY[k];
                pos[k * 3 + 2] = curZ[k];
                continue;
            }
            for (let s = 0; s < steps; s++) {
                prevX[k] = curX[k];
                prevY[k] = curY[k];
                prevZ[k] = curZ[k];
                velX[k] = (velX[k] + (targetX - curX[k]) * 9 * PHYSICS_STEP) * 0.86;
                velY[k] = (velY[k] + (targetY - curY[k]) * 9 * PHYSICS_STEP) * 0.86;
                velZ[k] = (velZ[k] + (targetZ - curZ[k]) * 9 * PHYSICS_STEP) * 0.86;
                if (pointer.inside) {
                    const dx = curX[k] - pointer.x;
                    const dy = curY[k] - pointer.y;
                    const distSq = dx * dx + dy * dy;
                    if (distSq < 14000) {
                        const dist = Math.sqrt(distSq) + 1;
                        const push = (1 - dist / 118) * 900;
                        velX[k] += (dx / dist) * push * PHYSICS_STEP;
                        velY[k] += (dy / dist) * push * PHYSICS_STEP;
                    }
                }
                curX[k] += velX[k] * PHYSICS_STEP;
                curY[k] += velY[k] * PHYSICS_STEP;
                curZ[k] += velZ[k] * PHYSICS_STEP;
            }
            pos[k * 3] = prevX[k] + (curX[k] - prevX[k]) * blend;
            pos[k * 3 + 1] = prevY[k] + (curY[k] - prevY[k]) * blend;
            pos[k * 3 + 2] = prevZ[k] + (curZ[k] - prevZ[k]) * blend;
        }
        resize();
        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, pos);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.POINTS, 0, POINT_COUNT);
        if (!painted && canvas.value) {
            painted = true;
            canvas.value.style.opacity = "1";
        }
        if (reduced)
            stop();
    }
    function start() {
        if (running || !gl)
            return;
        running = true;
        lastFrame = 0;
        accumulator = 0;
        raf = requestAnimationFrame(frame);
    }
    function stop() {
        if (!running)
            return;
        running = false;
        cancelAnimationFrame(raf);
    }
    function onPointerMove(event) {
        if (!host.value)
            return;
        const rect = host.value.getBoundingClientRect();
        const fx = (event.clientX - rect.left) / rect.width;
        const fy = (event.clientY - rect.top) / rect.height;
        // Point positions are divided by uRes/2 in the vertex shader, so the pointer
        // has to be expressed in that same space or the push lands in the wrong place
        // on any panel that is not 720x860 (the mobile band, most obviously).
        pointer.x = ((fx * 2 - 1) * ((host.value.clientWidth || 720) / 2)) / fit;
        pointer.y = (-(fy * 2 - 1) * ((host.value.clientHeight || 860) / 2)) / fit;
        pointer.nx = fx - 0.5;
        pointer.ny = fy - 0.5;
        pointer.inside = true;
    }
    function onContextLost(event) {
        // Without this the loop keeps issuing draw calls against a dead context and
        // the shell never learns it should show the static mark instead.
        event.preventDefault();
        stop();
        gl = null;
        markFieldUnavailable.value = true;
    }
    function onPointerLeave() {
        pointer.inside = false;
    }
    function onVisibility() {
        if (document.hidden)
            stop();
        else if (onScreen)
            start();
    }
    onMounted(() => {
        if (!canvas.value || !host.value)
            return;
        // The dots are soft-edged sprites, so MSAA adds nothing visible, but resolving it on
        // the supersampled buffer every frame was the main cause of dropped frames.
        gl = canvas.value.getContext("webgl2", { alpha: false, antialias: false });
        if (!gl) {
            markFieldUnavailable.value = true;
            return;
        }
        const program = gl.createProgram();
        gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
        gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            markFieldUnavailable.value = true;
            gl = null;
            return;
        }
        gl.useProgram(program);
        resolution = gl.getUniformLocation(program, "uRes");
        pxPerCss = gl.getUniformLocation(program, "uPxPerCss");
        fitUniform = gl.getUniformLocation(program, "uFit");
        buildGeometry();
        posBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, pos, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
        const colBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, colBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, col, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(1);
        gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
        const seedBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, seedBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, seed, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(2);
        gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 0, 0);
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        gl.clearColor(9 / 255, 16 / 255, 28 / 255, 1);
        const image = new Image();
        image.onload = () => {
            if (!gl)
                return;
            if (!sampleMark(image)) {
                markFieldUnavailable.value = true;
                return;
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, colBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, col, gl.STATIC_DRAW);
            gl.bindBuffer(gl.ARRAY_BUFFER, seedBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, seed, gl.STATIC_DRAW);
            ready = true;
            startedAt = performance.now();
            resize();
            start();
        };
        image.onerror = () => {
            markFieldUnavailable.value = true;
        };
        // Deployed builds serve assets from the CDN, a different origin; without CORS the pixel read throws.
        image.crossOrigin = "anonymous";
        image.src = markUrl;
        canvas.value.addEventListener("webglcontextlost", onContextLost);
        host.value.addEventListener("mousemove", onPointerMove);
        host.value.addEventListener("mouseleave", onPointerLeave);
        document.addEventListener("visibilitychange", onVisibility);
        observer = new IntersectionObserver((entries) => {
            onScreen = entries[0].isIntersecting;
            if (onScreen && !document.hidden)
                start();
            else
                stop();
        }, { threshold: 0.05 });
        observer.observe(host.value);
        resizeObserver = new ResizeObserver(() => resize());
        resizeObserver.observe(host.value);
    });
    onBeforeUnmount(() => {
        stop();
        observer?.disconnect();
        resizeObserver?.disconnect();
        document.removeEventListener("visibilitychange", onVisibility);
        host.value?.removeEventListener("mousemove", onPointerMove);
        host.value?.removeEventListener("mouseleave", onPointerLeave);
        canvas.value?.removeEventListener("webglcontextlost", onContextLost);
        gl?.getExtension("WEBGL_lose_context")?.loseContext();
        gl = null;
    });
    return { markFieldUnavailable };
}
